import { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import PageNavigation from "../../pageNavigation/PageNavigation";
import { getPostBaseUrl, getPostLabelName, getPostNicknm, isOnlyAdmin } from "../../../constant/PostClass";
import PostApi from "../../../api/PostApi";
import { loginAlert } from "../../common/CustomAlert";
import { getRemovedTagContent } from "../../../util/StringUtil";
import { resolvePostProfileImg } from "../../../util/ProfileUtil";
import { TodayTimestampDisplay } from "../../common/TimestampDisplay";
import { shouldShowWriterMedal } from "../../../util/MembershipUtil";
import { resolvePostThumbnailUrl } from "../../../util/ImageService";

const PAGE_SIZE = 20;

const findCategory = (categories, postType) => {
    const category = categories.find((c) => c.postType === postType);
    return category ? category : categories[0];
};

const findLabel = (labels, label) => {
    if (!labels || !labels.length) return "";
    const curLabel = labels.find((l) => l.value === label);
    return curLabel ? curLabel.value : labels[0].value;
};

const normalizePostType = (type) => {
    if (!type) return "PT001";
    if (type.length === 4) {
        const suffix = ["PT00", "PT01"].includes(type) ? "1" : "0";
        return type + suffix;
    }
    return type;
};

const getParams = (search, key) => {
    const params = new URLSearchParams(search);
    return params.get(key);
};

const BoardListItem = ({ post, isNotice = false }) => {
    const nicknm = getPostNicknm(post);
    const labelName = getPostLabelName(post.post_category) || post.post_category || "None";
    const profileImg = resolvePostProfileImg(post);
    const thumbnailUrl = resolvePostThumbnailUrl(post);
    const detailUrl = `${getPostBaseUrl(post)}/post/detail/${post.post_id}`;

    return (
        <Link to={detailUrl} className={`board-list-item${isNotice ? " is-notice" : ""}`}>
            <div className="board-list-col">
                <span className="category-list-label">{labelName}</span>
            </div>

            <div className="board-list-content">
                <div className="board-list-title">
                    <strong>{post.post_title}</strong>
                    {post.post_has_img && <i className="icon icon-image" aria-label="이미지 있음"></i>}
                </div>
                <p className="board-list-desc">{getRemovedTagContent(post.content)}</p>

                <div className="board-list-meta">
                    <div className="board-list-stats-item">
                        <span>{nicknm}</span>
                        {shouldShowWriterMedal(post) && <i className="icon icon-medal"></i>}
                    </div>
                    <div className="board-list-stats-item">
                        <span>
                            <TodayTimestampDisplay timestamp={post.post_reg_date} />
                        </span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-heart"></i>
                        <span>{post.post_like_cnt}</span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-chat"></i>
                        <span>{post.post_comment_cnt}</span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-eye"></i>
                        <span>{post.post_visit}</span>
                    </div>
                </div>
            </div>

            <div className="board-list-author">
                <span className="profile-thumb">
                    <img src={profileImg} />
                </span>
                <span>{nicknm}</span>
                {shouldShowWriterMedal(post) && <i className="icon icon-medal"></i>}
            </div>

            <div className="board-list-stats">
                <div className="board-list-stats-item">
                    <span>
                        <TodayTimestampDisplay timestamp={post.post_reg_date} />
                    </span>
                </div>
                <div className="board-list-stats-item">
                    <i className="icon icon-eye"></i>
                    <span>{post.post_visit}</span>
                </div>
            </div>
            {thumbnailUrl ? (
                <div className="board-list-thumb">
                    <img src={thumbnailUrl} alt="썸네일" />
                </div>
            ) : null}
        </Link>
    );
};

const SimpleBoardContainer = (props) => {
    const { usePinPost = false, categories = [], postLabels = [] } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const type = searchParams.get("postType");
    const labelParam = searchParams.get("postLabel");
    const [category, setCategory] = useState(findCategory(categories, type));
    const [curLabel, setCurLabel] = useState(findLabel(postLabels, labelParam));
    const [isSearch, setIsSearch] = useState(false);
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [totalCnt, setTotalCnt] = useState(0);
    const [announcePosts, setAnnouncePosts] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const sectionAreaRef = useRef(null);
    const filterRef = useRef(null);

    const postType = normalizePostType(category?.postType);
    const postLabel = curLabel;
    const currentLabelName =
        postLabels.find((l) => l.value === curLabel)?.name || "전체";

    const handleSearchInput = (e) => setSearchInput(e.target.value);

    const getPosts = async (opts = {}) => {
        const { newPage = page, newSize = PAGE_SIZE, newSearchKeyword = searchInput } = opts;
        const rslt = await PostApi.getPosts(
            postType,
            newPage,
            newSize,
            false,
            null,
            newSearchKeyword,
            postLabel
        );
        setPosts(rslt.posts);
        setIsSearch((newSearchKeyword || "").trim().length > 0);
        getTotalCount(newSearchKeyword);
    };

    const handleSearchInputKeyup = (e) => {
        if (e.which !== 13) return;
        handleSearchBtn();
    };

    const handleSearchBtn = (e, keywordFromTotalSearch) => {
        const newSearchKeyword = keywordFromTotalSearch
            ? keywordFromTotalSearch
            : searchInput.trim();
        setPage(1);
        getPosts({ newPage: 1, newSearchKeyword });
        setSearchInput(newSearchKeyword);
    };

    const handleSearchCloseBtn = () => {
        setSearchInput("");
        setPage(1);
        getPosts({ newPage: 1, newSearchKeyword: "" });
    };

    const getTotalCount = async (newSearchKeyword = searchInput) => {
        const rslt = await PostApi.getPostTotalCount(newSearchKeyword, postType, postLabel);
        setTotalCnt(rslt.totalCnt);
    };

    const handlePageChange = (newPage) => {
        if (!newPage) return;
        setPage(newPage);
        if (sectionAreaRef.current) {
            const headerOffset = 100;
            const elementTop =
                sectionAreaRef.current.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: elementTop - headerOffset, behavior: "smooth" });
        }
    };

    const getAnnouncePosts = async () => {
        if (location.pathname === "/") return;
        const rslt = await PostApi.getAnnoucePosts(postType);
        if (rslt?.posts) setAnnouncePosts(rslt.posts);
    };

    const handleWriteBtn = () => {
        if (!user.userId) return loginAlert("로그인이 필요합니다다.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        const endfix = ["PT00", "PT01"].includes(category.postType) ? "1" : "0";
        const navPostType =
            category.postType.length === 4 ? category.postType + endfix : category.postType;
        navigate(`/post/write?postType=${navPostType}`);
    };

    const isAdmin = () => {
        if (!isOnlyAdmin(postType)) return true;
        return user?.userId && user.userType === "admin";
    };

    const handleCategoryChange = (cate) => {
        setSearchParams({ postType: cate.postType });
    };

    const handleLabelChange = (label) => {
        setCurLabel(label.value);
        setFilterOpen(false);
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("postType", category.postType);
            if (label.value) {
                params.set("postLabel", label.value);
            } else {
                params.delete("postLabel");
            }
            return params;
        });
    };

    const toggleFilter = () => setFilterOpen((prev) => !prev);

    useEffect(() => {
        const newCategory = findCategory(categories, type);
        setCategory(newCategory);
        if (type !== newCategory?.postType) {
            setCurLabel(postLabels.length ? postLabels[0].value : "");
        }
    }, [type, categories, postLabels]);

    useEffect(() => {
        setCurLabel(findLabel(postLabels, labelParam));
    }, [labelParam, postLabels]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        setSearchInput("");
        setPage(1);
        getAnnouncePosts();
        getPosts({ newPage: 1, newSearchKeyword: "" });

        const handleTotalSearchEvent = (e) => {
            handleSearchBtn(e, e.detail.totalSearchKeyword);
        };
        document.addEventListener("totalSearch", handleTotalSearchEvent);
        return () => document.removeEventListener("totalSearch", handleTotalSearchEvent);
    }, [postType, postLabel]);

    useEffect(() => {
        if (page === 1) return;
        getPosts({ newPage: page, newSearchKeyword: searchInput });
    }, [page]);

    useEffect(() => {
        if (getParams(location.search, "reload") === "true") {
            getPosts({ newPage: page, newSearchKeyword: searchInput });
        }
    }, [location.search]);

    const allPosts = [...announcePosts, ...posts];

    return (
        <div className="vh-content">
            <Outlet />
            <div className="section-area" ref={sectionAreaRef}>
                <div className="section-header">
                    {categories.length > 1 && (
                        <div className="section-category">
                            <ul>
                                {categories.map((cate) => (
                                    <li
                                        key={cate.postType}
                                        className={
                                            cate.postType === category.postType ? "active" : ""
                                        }
                                        onClick={() => handleCategoryChange(cate)}
                                    >
                                        {cate.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {postLabels.length > 0 && (
                        <div className="section-category">
                            <div
                                className={`category-filter${filterOpen ? " open" : ""}`}
                                ref={filterRef}
                            >
                                <button
                                    type="button"
                                    className="category-filter-current"
                                    aria-expanded={filterOpen}
                                    onClick={toggleFilter}
                                >
                                    <span>{currentLabelName}</span>
                                    <i className="icon icon-arrow-down"></i>
                                </button>

                                <div className="category-filter-dropdown">
                                    {postLabels.map((label) => (
                                        <button
                                            key={label.value || "all"}
                                            type="button"
                                            onClick={() => handleLabelChange(label)}
                                        >
                                            {label.name}
                                        </button>
                                    ))}
                                </div>

                                <div
                                    className="category-filter-scroll"
                                    role="tablist"
                                    aria-label="카테고리 선택"
                                >
                                    {postLabels.map((label) => (
                                        <button
                                            key={label.value || "all"}
                                            type="button"
                                            className={label.value === curLabel ? "active" : ""}
                                            onClick={() => handleLabelChange(label)}
                                        >
                                            {label.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="section-header-right">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="검색어를 입력해주세요"
                                className="search-url"
                                value={searchInput}
                                onChange={handleSearchInput}
                                onKeyUp={handleSearchInputKeyup}
                            />
                            <button type="button" className="btn btn-primary" onClick={handleSearchBtn}>
                                <i className="icon icon-search-bold"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {isSearch && (
                    <div className="search-result-summary">
                        <p className="end_txt">검색결과 {totalCnt}건</p>
                        <button
                            type="button"
                            className="search-result-close"
                            onClick={handleSearchCloseBtn}
                        >
                            검색닫기
                        </button>
                    </div>
                )}

                <div className="section-content">
                    <div className="board-list">
                        {allPosts.map((post) => (
                            <BoardListItem
                                key={`${post.post_id}-${post.post_category}`}
                                post={post}
                                isNotice={post.post_category === "PCA01"}
                            />
                        ))}
                    </div>
                </div>

                <PageNavigation
                    postType={postType}
                    postLabel={postLabel}
                    totalCount={totalCnt}
                    showCount={PAGE_SIZE}
                    onChange={handlePageChange}
                />
            </div>
        </div>
    );
};

export default SimpleBoardContainer;
